/**
 * /b/:slug/a/:agentId — an individual agent's branded landing page (PR C).
 *
 * Agents share this link directly (business cards, their own site, social).
 * Distinct from /b/:slug (the brokerage's own landing): the page identifies
 * THIS agent (name, phone, licence) alongside the sponsoring broker, and a
 * client who signs up here lands assigned to them (same V060 carriage
 * BrokeragePage uses for a domain-owned agent).
 */

import { CircleUserRound, Loader2, LogIn, Phone, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { inviteTokenFor, saveOnboardingAgent } from "../home/onboardingAgent";
import type { AuthNavState, Brand } from "./AuthBrand";
import { SigninRoot } from "./BrokeragePage";
import { ComplianceFooter } from "./ComplianceFooter";
import { IntakeForm } from "./IntakeForm";
import { identityApi, type AgentLandingResponse } from "./passkeyApi";
import "./SignIn.css";

type State =
  | { kind: "loading" }
  | { kind: "ready"; data: AgentLandingResponse }
  | { kind: "error"; message: string };

export function AgentLandingPage({
  slug: slugProp,
  agentId: agentIdProp,
  inPlace = false,
}: {
  /** Override the route's params when rendered in place at `/` (spec 011). */
  slug?: string;
  agentId?: string;
  inPlace?: boolean;
} = {}) {
  const params = useParams<{ slug: string; agentId: string }>();
  const slug = slugProp ?? params.slug;
  const agentId = agentIdProp ?? params.agentId;
  const [state, setState] = useState<State>({ kind: "loading" });
  const navigate = useNavigate();

  useEffect(() => {
    if (!slug || !agentId) {
      setState({ kind: "error", message: "No agent link in the URL." });
      return;
    }
    identityApi
      .getAgentLanding(slug, agentId)
      .then((data) => {
        // V060 carriage — the tenant block's agent_id/agent_name always carry
        // THIS agent on the agent-landing page (see tenant_agent_by_slug).
        saveOnboardingAgent({
          agentId: data.tenant.agent_id ?? data.agent.user_id,
          agentTenantId: data.tenant.tenant_id,
          agentName: data.tenant.agent_name ?? data.agent.full_name,
          agentPhone: data.agent.phone_display ?? data.agent.phone,
          // Spec 023 — this page's agent wins (precedence II), but an invite from
          // the same brokerage is still carried, so it is still marked used.
          inviteToken: inviteTokenFor(data.tenant.tenant_id),
        });
        setState({ kind: "ready", data });
      })
      .catch((err: unknown) =>
        setState({
          kind: "error",
          message: err instanceof Error ? err.message : "Agent not found.",
        }),
      );
  }, [slug, agentId]);

  if (state.kind === "loading") {
    return (
      <SigninRoot inPlace={inPlace}>
        <section className="signin__card">
          <div className="signin__brand">
            <Loader2 className="signin__spin" size={28} aria-hidden />
            <span>Loading…</span>
          </div>
        </section>
      </SigninRoot>
    );
  }

  if (state.kind === "error") {
    // Never a dead end — same "no half-built page" fallback as BrokeragePage.
    return (
      <SigninRoot inPlace={inPlace}>
        <section className="signin__card">
          <IntakeForm />
        </section>
      </SigninRoot>
    );
  }

  const { tenant, agent } = state.data;

  // A dual-licensed agent represents TWO brokerages, and both marks belong on
  // the card the client actually signs up on — not just the one whose slug is
  // in the URL. `secondary_brokerage` is the other side's full public identity
  // (same shape as tenant.brokerage), populated by the cross-tenant phone-match
  // lookup in identity-svc.
  const brands: Brand[] = [
    {
      name: tenant.brokerage?.legal_name ?? tenant.display_name,
      logoUrl: tenant.brokerage?.logo_url ?? null,
    },
  ];
  if (agent.secondary_brokerage) {
    brands.push({
      name:
        agent.secondary_brokerage.legal_name ??
        agent.secondary_brokerage_name ??
        "",
      logoUrl: agent.secondary_brokerage.logo_url,
    });
  }
  const authState: AuthNavState = {
    tenantId: tenant.tenant_id,
    brandName: agent.full_name,
    brands,
  };

  return (
    <SigninRoot inPlace={inPlace}>
      <section className="signin__card">
        <div
          className="signin__brand"
          style={{
            justifyContent: "center",
            marginBottom: "var(--rp-space-4)",
          }}
        >
          {agent.photo_url ? (
            <img
              src={agent.photo_url}
              alt=""
              aria-hidden
              className="signin__agent-photo"
            />
          ) : (
            <CircleUserRound
              size={128}
              aria-hidden
              style={{ color: "var(--rp-color-primary)" }}
            />
          )}
        </div>
        <h1
          style={{
            textAlign: "center",
            // A long name wraps (spec 013). "anywhere" also lets a single
            // over-long word break, and is what lets the grid column shrink.
            // The size stays inline: Modern's stylesheet asks for 32px, which
            // would break TREC's half-size rule for the footer's broker name.
            overflowWrap: "anywhere",
            fontSize: "clamp(1.1rem, 6vw, 1.5rem)",
          }}
        >
          Let's work with {agent.full_name}
        </h1>
        {agent.phone && (
          <p className="signin__subtitle" style={{ textAlign: "center" }}>
            <a
              href={`tel:${agent.phone}`}
              style={{
                color: "inherit",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Phone size={14} aria-hidden />{" "}
              {agent.phone_display ?? agent.phone}
            </a>
          </p>
        )}
        <p className="signin__subtitle" style={{ textAlign: "center" }}>
          Sign in to your client portal or create a new account.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <button
            type="button"
            className="signin__cta rp-btn rp-btn--primary"
            onClick={() => void navigate("/sign-up", { state: authState })}
          >
            <UserPlus size={18} aria-hidden />
            Create account
          </button>
          <button
            type="button"
            className="signin__cta rp-btn"
            onClick={() => void navigate("/login", { state: authState })}
          >
            <LogIn size={18} aria-hidden />
            Sign in
          </button>
        </div>

        <p className="signin__footnote">
          Your account is secured with a passkey — no password to remember.
        </p>

        <ComplianceFooter tenant={tenant} agent={agent} />
      </section>
    </SigninRoot>
  );
}
